import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react';
import { Card, Col } from 'react-bootstrap';

import QueryErrorState from 'components/DesignSystem/QueryErrorState';
import Snackbar from 'components/DesignSystem/Snackbar';
import CentralizedSpinner from 'components/CentralizedSpinner';
import DoubleSlider from 'components/DesignSystem/Slider/DoubleSlider';
import useChartVariable from 'hooks/Meteorology/Agro/WaterBalanceSequential/DetailedView/useChartVariable';
import useGraphData from 'hooks/Meteorology/Agro/WaterBalanceSequential/DetailedView/useGraphData';
import useParams from 'hooks/Meteorology/Agro/WaterBalanceSequential/DetailedView/useParams';
import useWaterBalanceChartSubtitle from 'hooks/Meteorology/Agro/useWaterBalanceChartSubtitle';
import ChartDisplay from './ChartDisplay';
import ChartControls from './ChartControls';

import './index.scss';

export default function DetailedViewCard() {
  const dvImages = useRef<HTMLDivElement>(null);
  const params = useParams();
  const {
    chartOptions,
    selectedChartVariables,
    isLoading: variablesLoading,
  } = useChartVariable();
  const { selectAll } = useChartVariable();
  const selectedVariableNames = selectedChartVariables
    .map((variableId) => chartOptions.find((o) => o.id === variableId)?.value)
    .filter((v) => v) as string[];
  const {
    graphData, runDate, queriedLocationId, isFetching: graphDataLoading, queryError, onRetry,
  } = useGraphData();
  const subtitle = useWaterBalanceChartSubtitle({
    series: graphData, runDate, locationId: queriedLocationId,
  });
  const charts = useMemo(
    () => graphData?.map((data) => ({
      ...data,
      label: chartOptions.find((o) => o.value === data.name)?.label || '',
    })),
    [graphData, chartOptions],
  );

  const hasChuvaAndTemperatura = useMemo(() => {
    const hasChuva = selectedVariableNames.includes('chuva');
    const hasTemperatura = selectedVariableNames.includes('temperatura');
    return hasChuva && hasTemperatura;
  }, [selectedVariableNames]);

  const filteredVariableNames = useMemo(
    () => selectedVariableNames.filter(
      (name) => name !== 'chuva' && name !== 'temperatura',
    ),
    [selectedVariableNames],
  );

  const [zoomStart, setZoomStart] = useState(0);
  const [zoomEnd, setZoomEnd] = useState(100);

  const setZoom = useCallback(
    ([start, end]: number[]) => {
      setZoomStart(start);
      setZoomEnd(end);
    },
    [setZoomStart, setZoomEnd],
  );

  const chuvaChart = charts?.find((d) => d.name === 'chuva');
  const temperaturaChart = charts?.find((d) => d.name === 'temperatura');

  const showWarning = !charts || (filteredVariableNames.length === 0 && !hasChuvaAndTemperatura);
  const warningMessage = !charts
    ? 'Utilize o painel de controle para realizar uma consulta.'
    : 'Selecione uma variável.';

  useEffect(() => {
    if (charts && selectedVariableNames.length === 0) selectAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charts, selectAll]);

  if (variablesLoading || graphDataLoading) {
    return <CentralizedSpinner />;
  }

  if (queryError) {
    return (
      <QueryErrorState
        title={queryError.data}
        message={queryError.description}
        onRetry={onRetry}
      />
    );
  }

  return (
    <Card className="shadow-sm m-5 forecast-instruction-card">
      <Col className="flex-fill pt-5 h-100">
        <ChartControls
          imageRef={dvImages}
          hasCharts={charts !== undefined && params !== undefined}
        />
        {graphDataLoading && <CentralizedSpinner />}
        {!showWarning && (
          <>
            <div className="agro-water-balance-sequential-detailed-view-chart-container">
              <div ref={dvImages} className="h-auto w-auto">
                {hasChuvaAndTemperatura && chuvaChart && temperaturaChart && (
                  <ChartDisplay
                    key="chuva-temperatura"
                    zoomStart={zoomStart}
                    zoomEnd={zoomEnd}
                    combinedData={{
                      chuva: chuvaChart,
                      temperatura: temperaturaChart,
                    }}
                    subtitle={subtitle}
                  />
                )}
                {filteredVariableNames.map((variableName) => (
                  <ChartDisplay
                    key={variableName}
                    zoomStart={zoomStart}
                    zoomEnd={zoomEnd}
                    graphData={charts.find(
                      (data) => data.name === variableName,
                    )}
                    subtitle={subtitle}
                  />
                ))}
              </div>
            </div>
            <div className="agro-detailed-view-x-slider">
              <DoubleSlider
                min={0}
                max={100}
                value={[zoomStart, zoomEnd]}
                setValue={setZoom}
              />
            </div>
          </>
        )}
        {showWarning && (
          <Snackbar className="my-4 mx-4">{warningMessage}</Snackbar>
        )}
      </Col>
    </Card>
  );
}
